import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";
import {Box, Button, IconButton, Modal, Skeleton, Stack, Tooltip, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  ArrowDataTransferHorizontalIcon, ArrowLeft01Icon, ArrowRight01Icon, Bug01Icon, Calendar03Icon, Cancel01Icon,
  CustomerSupportIcon, EyeIcon, HeadsetIcon, LanguageSkillIcon, PhoneIcon, Tag01Icon, UserEdit01Icon
} from "@hugeicons/core-free-icons";
import {useAtom} from "jotai";
import {toast} from "react-toastify";
import {SelectedConversationAtom} from "../../../project/dashboard/atoms";
import {ConversationRepository} from "../../../../repositories/ConversationRepository";
import {SignInContext} from "../../../../context/SignInContext";
import {useRegistryProject} from "../../RegistryProjectContext";
import useConfirm from "../../../../hooks/useConfirm";
import useCallConfirm from "../../../../hooks/useCallConfirm";
import {useWebrtc, WEBRTC_ROLES} from "../../../../context/WebrtcContext";
import {useStompWebsocket} from "../../../../hooks/useStompWebsocket";
import {useFlowProtection} from "../../../../hooks/useFlowProtection";
import {FlowProtectionButton} from "../../../../components/FlowProtectionButton";
import {SupervisionHistoryItem} from "../../../project/supervision-page/components/supervision-history-item";
import {ContactPhoneNumber} from "../../../project/supervision-page/components/contact-phone-number";
import {AVATAR_COLORS, initialsOf, SenderName, StatusBadge, TagPills, TransferNumber} from "./conversationUi";

const roundButtonSx = {
  width: 34,
  height: 34,
  borderRadius: "50% !important",
  border: "1px solid #ECECF3",
  color: "#4A4A63",
  bgcolor: "#fff",
  "&:hover": {bgcolor: "#F4F4FA"},
  "&.Mui-disabled": {opacity: 0.4, bgcolor: "#fff"},
};

const InfoItem = ({icon, label, children}) => (
  <Stack direction={"row"} alignItems={"center"} gap={1} sx={{minWidth: 0}}>
    <Box sx={{
      width: 30, height: 30, borderRadius: "9px", flexShrink: 0, display: "flex", alignItems: "center",
      justifyContent: "center", bgcolor: "#fff", border: "1px solid #ECECF3", color: "#8491F7",
    }}>
      <HugeiconsIcon icon={icon} size={15}/>
    </Box>
    <Box sx={{minWidth: 0}}>
      <Typography sx={{fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0"}}>{label}</Typography>
      <Box sx={{fontSize: 13, color: "#23264D", fontWeight: 500, minWidth: 0}}>{children}</Box>
    </Box>
  </Stack>
);

// Conversation details in the dashboard's style: header with caller, status and actions, an info strip, then the
// messages. flow: Firebase flow the conversation ran on (redirect vdn, phonebook);
// subProjectOf(conversation) / tagsOf(conversation): its sub project and tag names, as shown in the table.
// flowOf(conversation): optional, finds the flow when `flow` isn't given. A conversation opened with `messageId`
// scrolls to that message once the messages are loaded.
export const ConversationModal = ({flow: flowProp, flowOf, subProjectOf, tagsOf}) => {
  const [conversation, setConversation] = useAtom(SelectedConversationAtom);
  const flow = flowProp || (conversation && flowOf ? flowOf(conversation) : null) || null;
  const open = Boolean(conversation);
  const {currentUser} = useContext(SignInContext);
  const {project, role, members} = useRegistryProject();
  const webrtc = useWebrtc();
  // project ADMIN / MANAGER / AGENT call through the browser phone only (it must be Connected), others by VDN
  const useBrowserPhone = WEBRTC_ROLES.includes(role);
  const confirmCall = useCallConfirm("Do you want to initiate conversation");

  const [prev, setPrev] = useState(null);
  const [next, setNext] = useState(null);
  const [openContact, setOpenContact] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const abortRef = useRef(null);

  const flowUid = conversation?.flowUid || flow?.id;
  const [updatedValue] = useStompWebsocket(conversation?.status === "ACTIVE" ? `/topic/session/${conversation?.id}` : null);
  const {token: protectionToken, extend: extendProtection} = useFlowProtection();

  const confirmMonitoring = useConfirm(
    `This conversation is already being monitored by ${conversation?.monitoredBy || "someone"}`,
    `Are you sure you want to take over monitoring from ${conversation?.monitoredBy || "someone"}?`
  );

  const fetchMessages = useCallback(() => {
    if (!conversation?.id) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setMessagesLoading(true);
    ConversationRepository.getMessages(conversation.id, controller.signal)
      .then(response => {
        setMessages([...(response?.data || [])].sort((a, b) => a.id - b.id));
        if (protectionToken) extendProtection();
      })
      .catch(error => console.log(error))
      .finally(() => !controller.signal.aborted && setMessagesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id, flowUid, protectionToken]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages, updatedValue]);

  // Protected messages unlocked (or locked again): reload the open conversation, its sender comes unmasked.
  // Its messages reload through fetchMessages.
  const tokenRef = useRef(protectionToken);
  useEffect(() => {
    if (tokenRef.current === protectionToken) return;
    tokenRef.current = protectionToken;
    if (!conversation?.id) return;
    const id = conversation.id;
    ConversationRepository.getConversation({id})
      .then(response => response?.data && setConversation(current => current?.id === id ? {...current, ...response.data} : current))
      .catch(() => {
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protectionToken]);

  useEffect(() => {
    if (!conversation?.messageId || messages.length === 0) return;
    const timeout = setTimeout(() => document.getElementById(`message-${conversation.messageId}`)
      ?.scrollIntoView({behavior: "smooth", block: "center"}), 150);
    return () => clearTimeout(timeout);
  }, [conversation?.messageId, messages]);

  useEffect(() => {
    try {
      setShowDebug(JSON.parse(localStorage.getItem("showDebug")) || false);
    } catch (e) {
      setShowDebug(false);
    }
  }, [conversation?.id]);

  useEffect(() => {
    setPrev(null);
    setNext(null);
    if (!conversation?.id) return;
    ConversationRepository.getPrevAndNext({id: conversation.id, flow: conversation.flowUid})
      .then(response => {
        setPrev(response?.data?.prev || null);
        setNext(response?.data?.next || null);
      })
      .catch(() => {
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id]);

  // the user's VDN from the project members (the flow's vdnManager is no longer updated)
  const agentVdn = members.find(member => member.username === currentUser?.username)?.vdn
    || flow?.configuration?.redirect_vdn;

  const handoverToVdn = () => ConversationRepository.handoverToAgent({id: conversation?.id, username: currentUser?.username, vdn: agentVdn})
    .then(response => {
      setConversation(current => response?.data || current);
      toast.success("Successfully handed over to agent");
    })
    .catch(() => toast.error("Failed to hand over to agent"));

  // to this browser for browser phone roles (the conversation becomes HANDOVER, pushed over the websocket), to the
  // VDN otherwise
  const handleHandover = () => {
    if (!useBrowserPhone) return handoverToVdn();
    return webrtc.takeOverCall({projectId: project?.id, conversationId: conversation?.id});
  };

  const callCustomerByVdn = async () => {
    if (!agentVdn || !conversation?.sender) {
      toast.info("No VDN configured for you in this project");
      return;
    }
    if (!(await confirmCall({title: `Do you want to initiate conversation with ${conversation.sender}`}))) return;
    const body = {
      username: currentUser?.username,
      vdn: agentVdn,
      phone_number: conversation.sender,
      initiate_call_api: flow?.configuration?.initiate_call_api,
    };
    ConversationRepository.initiateConversation({body, params: {conversation_id: conversation.id, flow_uid: flowUid}})
      .then(response => toast.success(response?.data?.message || `Initiated conversation with ${body.phone_number}`))
      .catch(error => toast.error(error?.message || `Failed to initiate conversation with ${body.phone_number}`));
  };

  const callCustomer = async () => {
    if (useBrowserPhone) {
      if (!(await confirmCall({title: `Do you want to call ${conversation?.sender || "the customer"} from your browser`}))) return;
      return webrtc.callCustomer({
        projectId: project?.id, conversationId: conversation?.id, phoneNumber: conversation?.sender,
      });
    }
    return callCustomerByVdn();
  };

  const monitorConversation = async () => {
    if (conversation?.monitoredBy && conversation?.monitoredBy !== currentUser?.username) {
      if (!(await confirmMonitoring())) return;
    }
    ConversationRepository.monitorConversation({id: conversation?.id, username: currentUser?.username, flowUid})
      .then(response => {
        setConversation(current => response?.data || current);
        toast.success("You are now monitoring this conversation");
      })
      .catch(() => console.log("Failed to monitor conversation"));
  };

  const name = conversation?.senderName || flow?.phonebook?.[conversation?.sender] || conversation?.sender;
  const initials = initialsOf(name);
  const avatar = AVATAR_COLORS[(Number(conversation?.id) || 0) % AVATAR_COLORS.length];
  const language = conversation ? subProjectOf?.(conversation) : null;
  const tags = useMemo(() => (conversation ? tagsOf?.(conversation) || [] : []), [conversation, tagsOf]);
  const isMonitoredByMe = conversation?.monitoredBy && conversation.monitoredBy === currentUser?.username;

  return (
    <>
      <ContactPhoneNumber
        conversation={conversation}
        open={openContact}
        handleClose={() => setOpenContact(false)}
        onSaved={(saved) => setConversation(current => current ? {...current, senderName: saved?.senderName} : current)}
      />
      <Modal open={open} onClose={() => setConversation(null)} aria-labelledby={"conversation-modal-title"}>
        <Box sx={{
          position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: {xs: "calc(100% - 24px)", md: "min(1100px, 92vw)"}, height: {xs: "calc(100% - 24px)", md: "86vh"},
          display: "flex", flexDirection: "column", overflow: "hidden", outline: "none",
          bgcolor: "#fff", borderRadius: "20px", boxShadow: "0 24px 70px rgba(35, 38, 77, 0.22)",
        }}>
          {/* Header */}
          <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{md: "center"}} gap={2}
                 sx={{px: 3, pt: 2.5, pb: 2}}>
            <Stack direction={"row"} alignItems={"center"} gap={1.5} sx={{minWidth: 0}}>
              <Box sx={{
                width: 44, height: 44, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: 15, fontWeight: 700, bgcolor: avatar.bg, color: avatar.color,
              }}>
                {initials || <HugeiconsIcon icon={CustomerSupportIcon} size={20}/>}
              </Box>
              <Box sx={{minWidth: 0}}>
                <Stack direction={"row"} alignItems={"center"} gap={1} sx={{minWidth: 0}}>
                  <SenderName name={name} sender={conversation?.sender}
                              sx={{
                                fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em",
                                maxWidth: {xs: 200, md: 280},
                              }}/>
                  <TransferNumber conversation={conversation} size={"medium"}/>
                  <Tooltip title={conversation?.senderName ? "Edit contact" : "Add as contact"}>
                    <IconButton size={"small"} onClick={() => setOpenContact(true)} sx={{color: "#8A8AA0"}}>
                      <HugeiconsIcon icon={UserEdit01Icon} size={16}/>
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Stack direction={"row"} alignItems={"center"} gap={1} flexWrap={"wrap"} sx={{mt: 0.25}}>
                  <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>#{conversation?.id}</Typography>
                  {conversation?.senderName && conversation?.sender && (
                    <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                      <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>·</Typography>
                      <SenderName name={conversation.sender} sender={conversation.sender}
                                  sx={{fontSize: 12, color: "#8A8AA0"}}/>
                    </Stack>
                  )}
                  {conversation && <StatusBadge conversation={conversation}/>}
                </Stack>
              </Box>
            </Stack>

            <Stack direction={"row"} alignItems={"center"} gap={1} flexWrap={"wrap"}>
              <Tooltip title={"Previous conversation"}>
                <span>
                  <IconButton sx={roundButtonSx} disabled={!prev} onClick={() => setConversation(prev)}>
                    <HugeiconsIcon icon={ArrowLeft01Icon} size={16}/>
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={"Next conversation"}>
                <span>
                  <IconButton sx={roundButtonSx} disabled={!next} onClick={() => setConversation(next)}>
                    <HugeiconsIcon icon={ArrowRight01Icon} size={16}/>
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={showDebug ? "Hide debug messages" : "Show debug messages"}>
                <IconButton
                  sx={{...roundButtonSx, ...(showDebug ? {color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.12)"} : {})}}
                  onClick={() => setShowDebug(current => {
                    localStorage.setItem("showDebug", JSON.stringify(!current));
                    return !current;
                  })}
                >
                  <HugeiconsIcon icon={Bug01Icon} size={16}/>
                </IconButton>
              </Tooltip>
              <FlowProtectionButton/>
              <Button
                onClick={monitorConversation}
                startIcon={<HugeiconsIcon icon={EyeIcon} size={16}/>}
                sx={{
                  height: 34, px: 1.75, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600,
                  color: "#23264D", border: "1px solid #E4E4EE", bgcolor: "#fff", "&:hover": {bgcolor: "#F4F4FA"},
                }}
              >
                {isMonitoredByMe ? "Supervising" : "Supervise"}
              </Button>
              {conversation?.type !== "CHAT_BOT" && conversation?.status !== "ACTIVE" && (
                <Button
                  onClick={callCustomer}
                  startIcon={<HugeiconsIcon icon={PhoneIcon} size={16}/>}
                  sx={{
                    height: 34, px: 1.75, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600,
                    color: "#23264D", border: "1px solid #E4E4EE", bgcolor: "#fff", "&:hover": {bgcolor: "#F4F4FA"},
                  }}
                >
                  Call customer
                </Button>
              )}
              <Button
                disabled={conversation?.status !== "ACTIVE"}
                onClick={handleHandover}
                startIcon={<HugeiconsIcon icon={useBrowserPhone ? HeadsetIcon : CustomerSupportIcon} size={16}/>}
                sx={{
                  height: 34, px: 1.75, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600,
                  color: "#fff", bgcolor: "#23264D", "&:hover": {bgcolor: "#33376B"},
                  "&.Mui-disabled": {color: "#fff", bgcolor: "#C9CAD8"},
                }}
              >
                {useBrowserPhone ? "Take over call" : "Handover"}
              </Button>
              <Tooltip title={"Close"}>
                <IconButton sx={{...roundButtonSx, border: 0}} onClick={() => setConversation(null)}>
                  <HugeiconsIcon icon={Cancel01Icon} size={18}/>
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Info strip */}
          <Box sx={{
            mx: 3, mb: 2, p: 1.5, borderRadius: "14px", bgcolor: "#FAFAFD", border: "1px solid #F0F0F6",
            display: "grid", gap: 2, gridTemplateColumns: {xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))"},
          }}>
            <InfoItem icon={Calendar03Icon} label={"STARTED"}>
              {conversation?.dateCreated ? moment(conversation.dateCreated).format("DD MMM YYYY, HH:mm") : "—"}
            </InfoItem>
            <InfoItem icon={LanguageSkillIcon} label={"LANGUAGE"}>
              {language?.languageName || language?.languageCode || conversation?.language || "—"}
            </InfoItem>
            <InfoItem icon={ArrowDataTransferHorizontalIcon} label={"SUPERVISED BY"}>
              {conversation?.monitoredBy || "Nobody"}
            </InfoItem>
            <InfoItem icon={Tag01Icon} label={"TAGS"}>
              <TagPills names={tags}/>
            </InfoItem>
          </Box>

          {/* Messages */}
          <Box sx={{flex: 1, minHeight: 0, overflowY: "auto", bgcolor: "#FAFAFD", borderTop: "1px solid #F0F0F6", px: {xs: 1.5, md: 3}, py: 2}}>
            {messagesLoading && messages.length === 0 ? (
              <Stack gap={2} sx={{minHeight: 240}}>
                {[0.6, 0.4, 0.7, 0.5].map((width, index) => (
                  <Skeleton key={index} variant={"rounded"} height={48}
                            sx={{width: `${width * 100}%`, borderRadius: "14px", alignSelf: index % 2 ? "flex-end" : "flex-start"}}/>
                ))}
              </Stack>
            ) : conversation && (
              <SupervisionHistoryItem
                conversation={conversation}
                messages={messages}
                project={flow}
                loadDuration={true}
                showUserTalking={true}
                showDebug={showDebug}
                updateConversation={(changes) => setConversation(current => ({...current, ...changes}))}
                fetchMessages={fetchMessages}
              />
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
};
